import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Check, MessageSquare } from 'lucide-react';
import { createStripeCustomer } from '@/api/stripe';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  stripe_price_id: string;
  price: number;
  features: string[];
  max_chatbots: number;
  max_conversations_per_month: number | null;
  is_active: boolean;
  trial_period_days?: number;
}

const PortalSignup: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState<'account' | 'plan'>('account');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });

  const { data: plans } = useQuery({
    queryKey: ["activePlans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plans")
        .select("*")
        .eq("is_active", true)
        .order("price");

      if (error) throw error;
      return data as Plan[];
    },
  });

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setStep('plan');
  };

  const handlePlanSelection = async (planId: string) => {
    setSelectedPlanId(planId);
    setIsLoading(true);

    try {
      // Create the user account first
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
          },
          emailRedirectTo: `${window.location.origin}/portal/auth?verified=true`
        }
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

      // Update user profile with name and plan assignment
      const selectedPlan = plans?.find(p => p.id === planId);
      const profileUpdateData = {
        name: formData.name,
        plan_id: planId || null,
        subscription_status: selectedPlan && selectedPlan.price > 0 ? 'pending' : 'free',
      };

      const { error: profileError } = await supabase
        .from('user_profiles')
        .update(profileUpdateData)
        .eq('id', authData.user.id);

      if (profileError) {
        console.error('Failed to update profile:', profileError);
        // Don't throw here, continue with checkout
      } else {
        console.log('✅ User profile updated with plan:', selectedPlan?.name || 'Free');
      }

      // Create Stripe customer automatically
      try {
        console.log('Creating Stripe customer for new user...');
        await createStripeCustomer(formData.email, formData.name);
        console.log('Stripe customer created successfully');
      } catch (stripeError) {
        console.error('Failed to create Stripe customer:', stripeError);
        // Check if it's a module loading error
        if (stripeError.message?.includes('Failed to fetch dynamically imported module')) {
          console.warn('Stripe module loading issue - please refresh the page and try again');
          toast({
            title: "Module Loading Issue",
            description: "Please refresh the page and try again. The development server may have restarted.",
            variant: "destructive",
          });
          return;
        }
        // Don't throw here for other errors, continue with signup process
        // Admin can create Stripe customer later if needed
      }

      // If a paid plan is selected, redirect to checkout
      if (selectedPlan && selectedPlan.price > 0) {
        try {
          // Check if we have Stripe environment variables configured
          const hasStripeConfig = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

          if (hasStripeConfig && selectedPlan.stripe_price_id) {
            // Production mode: redirect to actual Stripe checkout
            toast({
              title: "Redirecting to Checkout",
              description: `Setting up ${selectedPlan.name} plan...`,
            });

            // Use Stripe.js redirectToCheckout for immediate redirect
            const { initializeStripe } = await import('@/api/stripe');
            const stripe = await initializeStripe();

            if (stripe) {
              console.log('Redirecting to Stripe checkout with price ID:', selectedPlan.stripe_price_id);

              // Direct redirect to Stripe checkout
              const { error } = await stripe.redirectToCheckout({
                lineItems: [{
                  price: selectedPlan.stripe_price_id,
                  quantity: 1,
                }],
                mode: 'subscription',
                successUrl: `${window.location.origin}/portal/billing?success=true&plan=${planId}&user=${authData.user.id}`,
                cancelUrl: `${window.location.origin}/portal/billing?canceled=true`,
                customerEmail: authData.user.email,
              });

              if (error) {
                console.error('Stripe redirect error:', error);
                toast({
                  title: "Checkout Error",
                  description: `Stripe error: ${error.message}`,
                  variant: "destructive",
                });
                navigate('/portal/billing?error=stripe');
              }
              return;
            } else {
              throw new Error('Failed to initialize Stripe');
            }
          } else {
            // Demo mode: show success message (for development/testing)
            toast({
              title: "Account Created!",
              description: `Selected ${selectedPlan.name} plan. Stripe not configured - demo mode.`,
            });
            navigate('/portal/billing?demo=true');
            return;
          }
        } catch (error) {
          console.error('Stripe checkout error:', error);
          toast({
            title: "Checkout Error",
            description: `Error: ${error.message}`,
            variant: "destructive",
          });
          navigate('/portal/billing?error=checkout');
          return;
        }
      } else {
        // Free plan or no plan selected, redirect to portal
        toast({
          title: "Account Created!",
          description: "Please check your email to verify your account.",
        });
        navigate('/portal/auth?message=check-email');
      }

    } catch (error: any) {
      console.error('Signup error:', error);
      toast({
        title: 'Signup Failed',
        description: error.message || 'Failed to create account',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderAccountForm = () => (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Create Your Account</CardTitle>
        <CardDescription>
          Get started with OfferChat.ai by creating your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleAccountSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={6}
            />
          </div>
          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              minLength={6}
            />
          </div>
          <Button type="submit" className="w-full">
            Continue to Plan Selection
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Button
            variant="link"
            onClick={() => navigate('/portal/auth')}
          >
            Already have an account? Sign in
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderPlanSelection = () => (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">Choose Your Plan</h1>
        <p className="text-gray-600">Select the plan that best fits your needs</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3 mb-8">
        {plans?.map((plan) => (
          <Card 
            key={plan.id} 
            className={`p-6 cursor-pointer transition-all ${
              selectedPlanId === plan.id ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'
            }`}
            onClick={() => setSelectedPlanId(plan.id)}
          >
            <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
            <div className="mb-4">
              <p className="text-3xl font-bold">
                ${plan.price}<span className="text-sm font-normal text-gray-600">/mo</span>
              </p>
              {plan.trial_period_days && plan.trial_period_days > 0 && (
                <p className="text-sm text-green-600 font-medium mt-1">
                  {plan.trial_period_days}-day free trial
                </p>
              )}
            </div>
            {plan.description && (
              <p className="text-gray-600 mb-4">{plan.description}</p>
            )}
            <ul className="space-y-2 mb-6">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      <div className="flex justify-center space-x-4">
        <Button
          variant="outline"
          onClick={() => setStep('account')}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          onClick={() => selectedPlanId && handlePlanSelection(selectedPlanId)}
          disabled={!selectedPlanId || isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Create Account & Continue'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        <div className="text-center mb-8">
          <MessageSquare className="mx-auto h-12 w-12 text-primary" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Welcome to OfferChat.ai
          </h2>
        </div>
        
        {step === 'account' ? renderAccountForm() : renderPlanSelection()}
      </div>
    </div>
  );
};

export default PortalSignup;
